// src/AppAlt.jsx
import React, { useEffect, useRef, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";

// Auth wiring
import { watchAuth, collectRedirectResultIfAny } from "./services/auth";
import { ensureUserDoc } from "./services/users";

// Providers
import { AuthProvider } from "./contexts/AuthContext";
import { OrdersProvider } from "./contexts/OrdersContext";
import { LogosQueueProvider } from "./contexts/LogosQueueContext.tsx";
import { ToastProvider } from "./contexts/ToastContext";

// Shared UI
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ProtectedRoute from "./components/ProtectedRoute";
import SkipLinks from "./components/SkipLinks";
import MobileBottomNav from "./components/MobileBottomNav";
import ChatBot from "./components/ChatBot";

// Styles & A11y
import "./style/Site.css";
import "./style/animations.css";
import "./a11y/a11y.css";
import A11yToolkit from "./a11y/A11yToolkit";
import ReadAloud from "./a11y/ReadAloud";
import A11yFab from "./a11y/A11yFab";

// Pages - טוענים בצורה דינמית (Code Splitting)
const HomePage = lazy(() => import("./pages/HomePage"));
const Catalog = lazy(() => import("./pages/Catalog"));
const WorkwearCatalog = lazy(() => import("./pages/WorkwearCatalog"));
const SafetyCatalog = lazy(() => import("./pages/SafetyCatalog"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Cart = lazy(() => import("./pages/Cart"));
const Checkout = lazy(() => import("./pages/Checkout"));
const Contact = lazy(() => import("./pages/Contact"));
const FAQ = lazy(() => import("./pages/FAQ"));
const About = lazy(() => import("./pages/About"));
const Account = lazy(() => import("./pages/Account"));
const NotFound = lazy(() => import("./pages/NotFound"));
const OrderDetail = lazy(() => import("./pages/OrderDetail"));
const Orders = lazy(() => import("./pages/Orders"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const Legal = lazy(() => import("./pages/Legal"));

// קומפוננטת Loading
function PageLoader() {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '60vh',
      fontSize: '1.2rem',
      color: '#666'
    }}>
      <div>טוען...</div>
    </div>
  );
}

/** ====================== קבועים ====================== */
const GA_ID = "G-SHQSKGKY2C";
const IS_PROD = process.env.NODE_ENV === "production";

/** מגלגל לראש בכל ניווט */
function ScrollToTop() {
  const { pathname, search, hash } = useLocation();
  useEffect(() => {
    if (hash) return;
    // גלילה מיידית לראש העמוד בכל שינוי ניווט
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [pathname, search, hash]);
  return null;
}

/** שולח page_view ל-GA4 בכל שינוי נתיב (SPA) */
function GAListener() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    if (!window.gtag) return;
    window.gtag("config", GA_ID, { page_path: pathname + (search || "") });
  }, [pathname, search]);
  return null;
}

/** Helmet-Root דינמי: canonical, OG/Twitter, GA scripts, preconnect ועוד */
function RootSEO() {
  const { pathname, search } = useLocation();
  const base =
    (typeof window !== "undefined" && window.location?.origin) ||
    "https://karina.co.il";
  const url = base + pathname + (search || "");

  const ogImage = `${base}/img/logo.png`;
  const title = "קארינה חולצות מודפסות";
  const description =
    "תצוגת פיתוח לדפים החדשים של קארינה: קטלוג, מוצר, עגלה, תשלום ועוד.";

  return (
    <Helmet htmlAttributes={{ lang: "he", dir: "rtl" }}>
      <title>{title}</title>
      <meta name="description" content={description} />

      <link rel="canonical" href={url} />

      <meta property="og:type" content="website" />
      <meta property="og:locale" content="he_IL" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      <meta name="theme-color" content="#ffffff" />
      <link rel="icon" href="/favicon.ico" />

      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />

      <script async src={`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`}></script>
      <script>
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${GA_ID}');
        `}
      </script>

      {/* Meta Pixel Code */}
      <script>
        {`
          !function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '1463072928112951');
          fbq('track', 'PageView');
        `}
      </script>
      <noscript>
        {`<img height="1" width="1" style="display:none"
        src="https://www.facebook.com/tr?id=1463072928112951&ev=PageView&noscript=1"
        />`}
      </noscript>
      {/* End Meta Pixel Code */}

      {!IS_PROD && <meta name="robots" content="noindex,nofollow" />}
    </Helmet>
  );
}

export default function AppAlt() {
  // ---- חיווט יצירת/סנכרון users_prod/{uid} ----
  const bootstrapped = useRef(false);

  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;

    // 1) איסוף תוצאת Redirect (iOS / in-app browsers)
    (async () => {
      try {
        const rr = await collectRedirectResultIfAny();
        const user = rr?.user;
        if (!user) return;

        // דילוג על אנונימי/ללא UID
        if (user.isAnonymous || !user.uid) {
          console.debug("[redirect] skip ensureUserDoc (anonymous/no uid)");
          return;
        }

        await ensureUserDoc(user);
      } catch (e) {
        console.error("[redirect->ensureUserDoc]", e?.code, e?.message);
      }
    })();

    // 2) מאזין גלובלי ל-auth (כולל ריענון דף)
    const unsub = watchAuth(async (user) => {
      if (!user) return;

      // אם המשתמש אנונימי – דלג
      if (user.isAnonymous) {
        console.debug("[Auth] anonymous session -> skip ensureUserDoc");
        return;
      }

      // ודא שיש UID תקין
      if (!user.uid) {
        console.warn("[Auth] user without UID -> skip ensureUserDoc");
        return;
      }

      try {
        await ensureUserDoc(user);
      } catch (e) {
        console.error("[onAuthStateChanged->ensureUserDoc]", e?.code, e?.message);
      }
    });

    return () => unsub?.();
  }, []);

  // דמויות לתשובות מהירות בצ'אט (לבקשתך)
  async function handleChatSend(text) {
    const t = (text || "").toLowerCase();
    if (t.includes("מחיר") || t.includes("הצעת")) {
      return { role: "assistant", text: "בשמחה! השאירו פרטים וקובץ לוגו כאן: /contact ונחזיר הצעת מחיר מדויקת." };
    }
    if (t.includes("מידה") || t.includes("מידות")) {
      return { role: "assistant", text: "טבלת מידות נמצאת בכל דף מוצר. מומלץ למדוד חולצה קיימת ולהשוות לטבלה." };
    }
    if (t.includes("הדמיה") || t.includes("לוגו")) {
      return { role: "assistant", text: "אפשר להעלות לוגו בדף המוצר ולקבל הדמיה מיידית. קבצים מומלצים: PDF/SVG/AI או PNG שקוף 300dpi." };
    }
    return { role: "assistant", text: "קיבלתי: " + text };
  }

  return (
    <HelmetProvider>
      <AuthProvider>
        <OrdersProvider>
          <LogosQueueProvider>
            <ToastProvider>
              <BrowserRouter>
                <RootSEO />
                <SkipLinks />

                <Navbar />

              <main className="min-vh-100">
                <ScrollToTop />
                <GAListener />

                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    {/* ציבורי */}
                    <Route path="/" element={<HomePage />} />
                    <Route path="/catalog" element={<Catalog />} />
                    <Route path="/workwear" element={<WorkwearCatalog />} />
                    <Route path="/safety" element={<SafetyCatalog />} />
                  <Route path="/product/:slug" element={<ProductDetail />} />
                  <Route path="/cart" element={<Cart />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/faq" element={<FAQ />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/auth" element={<AuthPage />} />

                  {/* מוגן */}
                  <Route
                    path="/checkout"
                    element={
                      <ProtectedRoute>
                        <Checkout />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/account"
                    element={
                      <ProtectedRoute>
                        <Account />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/orders"
                    element={
                      <ProtectedRoute>
                        <Orders />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/orders/:orderId"
                    element={
                      <ProtectedRoute>
                        <OrderDetail />
                      </ProtectedRoute>
                    }
                  />

                  {/* 404 + legal */}
                  <Route path="*" element={<NotFound />} />
                  <Route path="/legal" element={<Legal />} />
                </Routes>
                </Suspense>
              </main>

              <Footer />
              <MobileBottomNav />
              <ChatBot />

              <div dir="ltr" id="a11y-fixed-layer">
                <A11yToolkit />
                <ReadAloud />
                <A11yFab />
              </div>
              </BrowserRouter>
            </ToastProvider>
          </LogosQueueProvider>
        </OrdersProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}
